/**
 * Shared Lists API service
 * Handles all shared list-related API requests
 */

import { get, post, put, del, buildQueryString } from './api';
import type {
  SharedList,
  CreateSharedListInput,
  SharedListInfo,
  SharedListPlacesResponse,
} from '@/types/sharedList';

/**
 * Get all shared lists (admin view)
 */
export async function getSharedLists(): Promise<SharedList[]> {
  const response = await get<{ lists: SharedList[] }>('/shared-lists');
  return response.lists;
}

/**
 * Create a new shared list
 */
export async function createSharedList(input: CreateSharedListInput): Promise<SharedList> {
  return post<SharedList>('/shared-lists', input);
}

/**
 * Get a shared list by ID (admin view)
 */
export async function getSharedList(id: string): Promise<SharedList> {
  return get<SharedList>(`/shared-lists/${id}`);
}

/**
 * Update a shared list
 */
export async function updateSharedList(
  id: string,
  input: Partial<CreateSharedListInput>
): Promise<SharedList> {
  return put<SharedList>(`/shared-lists/${id}`, input);
}

/**
 * Delete a shared list
 */
export async function deleteSharedList(id: string): Promise<void> {
  return del<void>(`/shared-lists/${id}`);
}

/**
 * Get public shared list info (check if password required)
 */
export async function getSharedListInfo(slug: string): Promise<SharedListInfo> {
  return get<SharedListInfo>(`/public/shared/${slug}/info`);
}

/**
 * Verify password for a shared list
 */
export async function verifySharedListPassword(
  slug: string,
  password: string
): Promise<boolean> {
  const response = await post<{ valid: boolean }>(`/public/shared/${slug}/verify`, {
    password,
  });
  return response.valid;
}

/**
 * Get places for a shared list (public)
 */
export async function getSharedListPlaces(
  slug: string,
  options?: {
    password?: string;
    limit?: number;
    offset?: number;
  }
): Promise<SharedListPlacesResponse> {
  const queryString = buildQueryString({
    password: options?.password,
    limit: options?.limit || 100,
    offset: options?.offset || 0,
  });
  return get<SharedListPlacesResponse>(`/public/shared/${slug}/places${queryString}`);
}

/**
 * Generate the full URL for a shared list
 */
export function getSharedListUrl(slug: string): string {
  return `${window.location.origin}/shared/${slug}`;
}
